import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../database/config';
import { slugify, getPostSlug } from '../../utils/slugify';
import SEO from '../SEO/index';

const BASE_URL = 'https://www.getbentobox.in';

// Converts a Firestore Timestamp, Date or date string to ISO 8601 for schema.org.
// Returns undefined when the value is missing or not a valid date, so the key is
// dropped from the JSON-LD instead of sending an invalid date.
const toISODateTime = (value) => {
    if (!value) return undefined;
    try {
        const d = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
        return isNaN(d.getTime()) ? undefined : d.toISOString();
    } catch (e) {
        return undefined;
    }
};

// Short readable date (e.g. "3 Nov 2026") for the visible "Updated" label.
const formatShortDate = (iso) => {
    try {
        return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch (e) {
        return '';
    }
};

const cardNumber = (id) => parseInt(String(id).replace('card', '')) || 0;

// Category colors
const categoryColors = {
    "Health": "bg-green-500",
    "Lifestyle": "bg-blue-500",
    "Food Safety": "bg-red-500",
    "Environment": "bg-emerald-500",
    "Recipes": "bg-orange-500",
    "Tips": "bg-purple-500",
    "Stories": "bg-pink-500",
    "Food": "bg-amber-500",
    "Technology": "bg-orange-500",
    "default": "bg-orange-500"
};

const BlogPostDetail = () => {
    const { postId } = useParams(); // The slug (e.g. "finding-the-perfect-tiffin-service-near-me-in-gurgaon")
    const [cardData, setCardData] = useState(null);
    const [postContent, setPostContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);

    useEffect(() => {
        const fetchBlogPost = async () => {
            try {
                // Fetch all blog cards to find the one matching the slug
                const blogsCollection = collection(db, 'Blogs');
                const blogsSnap = await getDocs(blogsCollection);

                let foundCard = null;
                let foundCardId = null;
                const allCards = [];

                blogsSnap.forEach((docSnap) => {
                    const docId = docSnap.id;
                    if (docId.startsWith('card')) {
                        const data = docSnap.data();
                        allCards.push({ id: docId, ...data });
                        // Match the fixed `slug` field (or heading), plus any old slugs kept after a rename
                        const matchesSlug = getPostSlug(data) === postId
                            || (Array.isArray(data.oldSlugs) && data.oldSlugs.includes(postId));
                        if (matchesSlug && !foundCard) {
                            foundCard = data;
                            foundCardId = docId;
                        }
                    }
                });

                if (foundCard && foundCardId) {
                    setCardData(foundCard);

                    // Related posts: same category first, then newest; never the current post
                    const others = allCards.filter((c) => c.id !== foundCardId && c.heading);
                    const byNewest = (a, b) => cardNumber(b.id) - cardNumber(a.id);
                    const sameType = others.filter((c) => c.type && c.type === foundCard.type).sort(byNewest);
                    const rest = others.filter((c) => !(c.type && c.type === foundCard.type)).sort(byNewest);
                    setRelatedPosts([...sameType, ...rest].slice(0, 3));

                    // Fetch post content using the actual card ID
                    const postRef = doc(db, 'blogPost', `${foundCardId}BlogPost`);
                    const postSnap = await getDoc(postRef);
                    if (postSnap.exists()) {
                        setPostContent(postSnap.data());
                    }
                } else {
                    setError('Blog post not found');
                }
            } catch (err) {
                console.error('Error fetching blog post:', err);
                setError('Failed to load blog post');
            } finally {
                setLoading(false);
            }
        };

        if (postId) {
            fetchBlogPost();
        }
    }, [postId]);

    // ── Build SEO data ────────────────────────────────────────────────────────
    const canonicalUrl = `${BASE_URL}/blog/${cardData ? getPostSlug(cardData) : postId}`;
    const seoTitle = cardData?.heading
        ? `${cardData.heading} | BentoBox Blog`
        : 'BentoBox Blog';
    const seoDescription = cardData?.summary
        ? cardData.summary.substring(0, 160)
        : 'Read insightful articles about home-cooked food, tiffin services, and healthy meal subscriptions in Gurgaon.';
    const seoOgImage = cardData?.img || `${BASE_URL}/og/default.jpg`;

    // Article structured data (schema.org) — helps Google index as Article
    const articleSchema = cardData ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: cardData.heading || '',
        description: seoDescription,
        image: seoOgImage,
        datePublished: toISODateTime(cardData.datePublished || cardData.date),
        dateModified: toISODateTime(cardData.dateModified || cardData.datePublished || cardData.date),
        author: {
            '@type': 'Person',
            name: cardData.author || 'BentoBox Team',
            url: `${BASE_URL}/author/${slugify(cardData.author || '')}`,
        },
        publisher: {
            '@type': 'Organization',
            name: 'BentoBox',
            logo: {
                '@type': 'ImageObject',
                url: `${BASE_URL}/data/logo2.png`,
            },
        },
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl,
        },
        url: canonicalUrl,
    } : null;

    // Breadcrumb structured data: Home > Blog > Post
    const breadcrumbSchema = cardData ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
            { '@type': 'ListItem', position: 3, name: cardData.heading || 'Post', item: canonicalUrl },
        ],
    } : null;

    // Visible "Updated" date, shown only when the post was edited on a later day
    const publishedIso = cardData ? toISODateTime(cardData.datePublished || cardData.date) : undefined;
    const modifiedIso = cardData ? toISODateTime(cardData.dateModified) : undefined;
    const showUpdated = Boolean(modifiedIso && (!publishedIso || modifiedIso.slice(0, 10) > publishedIso.slice(0, 10)));

    // ── Render ────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <>
                <SEO
                    pageName="blogpost"
                    customTitle="Loading... | BentoBox Blog"
                    customDescription="Read insightful articles about home-cooked food, tiffin services, and healthy meal subscriptions in Gurgaon."
                    customCanonical={canonicalUrl}
                />
                <section className="bg-white min-h-screen">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 flex items-center justify-center">
                        <div className="animate-pulse text-gray-500 text-lg">Loading post...</div>
                    </div>
                </section>
            </>
        );
    }


    if (error || !cardData) {
        return (
            <>
                <SEO
                    pageName="blog"
                    customTitle="Post Not Found | BentoBox Blog"
                    customCanonical={`${BASE_URL}/blog`}
                    indexPage={false}
                />
                <section className="bg-white min-h-screen">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Blog post not found'}</h1>
                        <Link to="/blog" className="text-orange-500 hover:text-orange-600 font-medium">
                            ← Back to Blog
                        </Link>
                    </div>
                </section>
            </>
        );
    }

    const categoryClass = categoryColors[cardData.type] || categoryColors["default"];

    return (
        <>
            {/* SEO: dynamic, per-post — canonical = exact slug URL */}
            <SEO
                pageName="blogpost"
                customTitle={seoTitle}
                customDescription={seoDescription}
                customCanonical={canonicalUrl}
                customOgImage={seoOgImage}
                customStructuredData={[articleSchema, breadcrumbSchema]}
            />

            <section className="!pt-12 md:!pt-16 bg-white min-h-screen">
                <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 md:pt-10 md:pb-16">
                    {/* Back Link */}
                    <div className="mb-8">
                        <Link to="/blog" className="text-gray-500 hover:text-orange-500 transition-colors text-sm flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                            </svg>
                            Back to Blog
                        </Link>
                    </div>

                    {/* Category Tag */}
                    <div className="mb-4">
                        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white ${categoryClass}`}>
                            {cardData.type}
                        </span>
                    </div>

                    {/* Title — h1 must be on every page, matching the SEO title's topic */}
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                        {cardData.heading}
                    </h1>

                    {/* Author & Date */}
                    <div className="flex items-center gap-3 mb-8">
                        <Link to={`/author/${slugify(cardData.author)}`} className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center overflow-hidden hover:ring-2 hover:ring-orange-300 transition-all">
                            {cardData.authorImg ? (
                                <img src={cardData.authorImg} alt={cardData.author} className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-orange-500 text-sm font-semibold">{cardData.author?.charAt(0) || 'A'}</span>
                            )}
                        </Link>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Link to={`/author/${slugify(cardData.author)}`} className="font-medium text-gray-900 hover:text-orange-500 transition-colors">
                                {cardData.author}
                            </Link>
                            <span>•</span>
                            <span>{cardData.date}</span>
                            {showUpdated && (
                                <>
                                    <span>•</span>
                                    <span>Updated {formatShortDate(modifiedIso)}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Featured Image */}
                    {(cardData.img || postContent?.img) && (
                        <div className="mb-10 rounded-2xl overflow-hidden shadow-lg">
                            <img
                                src={cardData.img || postContent.img}
                                alt={cardData.alt || cardData.heading}
                                className="w-full h-64 md:h-96 object-cover"
                                loading="eager"
                            />
                        </div>
                    )}

                    {/* Post Content */}
                    {postContent && (
                        <div className="prose prose-lg max-w-none">
                            {/* Dynamic Heading/Para Sections */}
                            {(() => {
                                const sections = [];
                                let i = 1;

                                while (postContent[`heading${i}`] || postContent[`para${i}`]) {
                                    const heading = postContent[`heading${i}`];
                                    const para = postContent[`para${i}`];

                                    sections.push(
                                        <div key={i} className={i > 1 ? 'mt-10' : ''}>
                                            {heading && (
                                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                                                    {heading}
                                                </h2>
                                            )}
                                            {para && (
                                                <div
                                                    className="text-gray-700 leading-relaxed mb-6 text-base md:text-lg blog-content"
                                                    dangerouslySetInnerHTML={{ __html: para }}
                                                />
                                            )}
                                        </div>
                                    );
                                    i++;
                                }

                                return sections;
                            })()}

                            {/* Quote */}
                            {postContent.quote && (
                                <blockquote className="my-10 px-6 py-8 bg-gray-50 border-l-4 border-gray-300 rounded-r-lg">
                                    <p className="text-gray-800 italic text-lg md:text-xl leading-relaxed">
                                        "{postContent.quote}"
                                    </p>
                                </blockquote>
                            )}
                        </div>
                    )}

                    {/* Share Section */}
                    <div className="mt-12 pt-8 border-t border-gray-200">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-4">
                                <span className="text-gray-600 font-medium">Share this article:</span>
                                <div className="flex items-center gap-3">
                                    <a
                                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(canonicalUrl)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Share on Facebook"
                                        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-orange-100 hover:text-orange-500 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
                                        </svg>
                                    </a>
                                    <a
                                        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(canonicalUrl)}&text=${encodeURIComponent(cardData.heading || '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Share on X (Twitter)"
                                        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-orange-100 hover:text-orange-500 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </a>
                                    <a
                                        href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(canonicalUrl)}&title=${encodeURIComponent(cardData.heading || '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Share on LinkedIn"
                                        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-orange-100 hover:text-orange-500 transition-colors"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <Link to="/blog" className="text-orange-500 hover:text-orange-600 font-medium flex items-center gap-2">
                                View all posts
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </Link>
                        </div>
                    </div>

                    {/* Related Posts */}
                    {relatedPosts.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-gray-200">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Related posts</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                {relatedPosts.map((post) => (
                                    <Link key={post.id} to={`/blog/${getPostSlug(post)}`} className="block group">
                                        {post.img && (
                                            <div className="rounded-xl overflow-hidden mb-3">
                                                <img
                                                    src={post.img}
                                                    alt={post.alt || post.heading}
                                                    className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <h3 className="text-base font-semibold text-gray-900 group-hover:text-orange-500 transition-colors line-clamp-2">
                                            {post.heading}
                                        </h3>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </article>
            </section>
        </>
    );
};

export default BlogPostDetail;
